import {Nullable, PaginationType} from "../../definitions/types";

export class PaginationModel {
    private readonly _isDefault: boolean;
    private readonly _pageNumber: number;
    private readonly _pageSize: number;
    private readonly _totalItemCount: number;
    private readonly _totalPageCount: number;
    private readonly _lastPageNumber: number;
    private readonly _hasNextPage: boolean;
    private readonly _hasPreviousPage: boolean;
    private readonly _isFirstPage: boolean;
    private readonly _isLastPage: boolean;

    constructor(public readonly pageInfo: Nullable<PaginationType>) {
        this._isDefault = pageInfo === null;
        this._pageNumber = pageInfo?.pageNumber ?? 0;
        this._pageSize = pageInfo?.pageSize ?? 0;
        this._totalItemCount = pageInfo?.totalItemCount ?? 0;
        this._totalPageCount = pageInfo?.totalPageCount ?? 0;
        this._lastPageNumber = pageInfo?.lastPage ?? 0;
        this._hasNextPage = pageInfo?.hasNextPage ?? false;
        this._hasPreviousPage = pageInfo?.hasPreviousPage ?? false;
        this._isFirstPage = pageInfo?.isFirstPage ?? false;
        this._isLastPage = pageInfo?.isLastPage ?? false;
    }

    /**
     * @method isDefault
     * @description Returns true if the pagination is the default pagination. (i.e. the arguments were null or undefined when the model was created.)
     * @returns {boolean}
     */
    public get isDefault(): boolean {
        return this._isDefault;
    }

    /**
     * @method pageNumber
     * @description Returns the zero-based page number of the pagination.
     * @returns {number}
     */
    public get pageNumber(): number {
        return this._pageNumber;
    }

    /**
     * @method startItemNumber
     * @description Returns the start item number of the pagination.
     * @returns {number}
     */
    public get startItemNumber(): number {
        return this._pageNumber * this._pageSize + 1;
    }

    /**
     * @method endItemNumber
     * @description Returns the end item number of the pagination.
     * @returns {number}
     */
    public get endItemNumber(): number {
        return Math.min(this._pageNumber * this._pageSize + this._pageSize, this._totalItemCount);
    }

    /**
     * @method itemRange
     * @description Returns the item range of the pagination.
     * @returns {string}
     */
    public get itemRange(): string {
        return `${this.startItemNumber} - ${this.endItemNumber}`;
    }

    /**
     * @method nextPageNumber
     * @description Returns the zero-based next page number of the pagination.
     * @returns {number}
     */
    public get nextPageNumber(): number {
        return this._pageNumber + 1;
    }

    /**
     * @method previousPageNumber
     * @description Returns the zero-based previous page number of the pagination.
     * @returns {number}
     */
    public get previousPageNumber(): number {
        return this._pageNumber - 1;
    }

    /**
     * @method displayPageNumber
     * @description Returns the one-based page number of the pagination.
     * @returns {number}
     */
    public get displayPageNumber(): number {
        return this._pageNumber + 1;
    }

    /**
     * @method pageSize
     * @description Returns the page size of the pagination.
     * @returns {number}
     */
    public get pageSize(): number {
        return this._pageSize;
    }

    /**
     * @method totalItemCount
     * @description Returns the total item count of the pagination.
     * @returns {number}
     */
    public get totalItemCount(): number {
        return this._totalItemCount;
    }

    /**
     * @method hasItems
     * @description Returns whether the pagination has items.
     * @returns {boolean}
     */
    public get hasItems(): boolean {
        return this._totalItemCount > 0;
    }

    /**
     * @method totalPageCount
     * @description Returns the total page count of the pagination.
     * @returns {number}
     */
    public get totalPageCount(): number {
        return this._totalPageCount;
    }

    /**
     * @method hasMorePages
     * @description Returns whether the pagination has more pages.
     * @returns {boolean}
     */
    public get hasMorePages(): boolean {
        return this._pageNumber < this._totalPageCount;
    }

    /**
     * @method lastPageNumber
     * @description Returns the last page number of the pagination.
     * @returns {number}
     */
    public get lastPageNumber(): number {
        return this._lastPageNumber;
    }

    /**
     * @method hasNextPage
     * @description Returns whether the pagination has a next page.
     * @returns {boolean}
     */
    public get hasNextPage(): boolean {
        return this._hasNextPage;
    }

    /**
     * @method hasPreviousPage
     * @description Returns whether the pagination has a previous page.
     * @returns {boolean}
     */
    public get hasPreviousPage(): boolean {
        return this._hasPreviousPage;
    }

    /**
     * @method isFirstPage
     * @description Returns whether the pagination is on the first page.
     * @returns {boolean}
     */
    public get isFirstPage(): boolean {
        return this._isFirstPage;
    }

    /**
     * @method isLastPage
     * @description Returns whether the pagination is on the last page.
     * @returns {boolean}
     */
    public get isLastPage(): boolean {
        return this._isLastPage;
    }
}